import { Link } from 'react-router-dom'
import { useMemo } from 'react'
import {ArrowUpRight, ShieldCheck, GraduationCap, Briefcase, Users, Star, Waves, CheckCircle2, Sparkles, Award, Heart, LifeBuoy, Compass, Quote} from 'lucide-react'
import JobCardHome from '@/components/JobCardHome'
import HomeCustomSection from '@/components/HomeCustomSection'
import HomeHeroSection from '@/components/HomeHeroSection'
import { jobs as staticJobs, type Job, type JobCategory } from '@/data/jobs'
import { useAllJobs, recruiterJobToJob, type RecruiterJob } from '@/hooks/useRecruiterJobs'
import { testimonials, faqs } from '@/data/content'
import type { HomeSection } from '@/hooks/useHomeSections'

const MISSION_BG = 'https://static.lumi.new/76/76bce1756c9416159dbd48eb33aaa081.webp'

// IDs des offres statiques pour déduplication face aux offres recruteurs DB
const STATIC_IDS = new Set(staticJobs.map((j) => j.id))

// Hook : retourne les N dernières offres d'une catégorie, fusionnant DB validées
// (useAllJobs) + statiques, dédupliquées et triées par date de publication desc.
function useLatestJobsByCategory(category: JobCategory, limit = 3): Job[] {
  const { jobs: dbJobs } = useAllJobs()
  return useMemo(() => {
    const dbConverted: Job[] = dbJobs
      .filter((rj) => !STATIC_IDS.has(rj._id))
      .filter((rj) => {
        const status = (rj as RecruiterJob & { validationStatus?: string }).validationStatus
        return !status || status === 'approved'
      })
      .map((rj) => recruiterJobToJob(rj as RecruiterJob))
    const merged: Job[] = [...dbConverted, ...staticJobs]
    return merged
      .filter((j) => j.category === category)
      .sort((a, b) => {
        const ta = new Date(a.publishedAt).getTime()
        const tb = new Date(b.publishedAt).getTime()
        return (Number.isNaN(tb) ? 0 : tb) - (Number.isNaN(ta) ? 0 : ta)
      })
      .slice(0, limit)
  }, [dbJobs, category, limit])
}

// Helper: html to inline-friendly text fallback
function StripHtml({ html, className }: { html: string; className?: string }) {
  return <div className={className} dangerouslySetInnerHTML={{ __html: html }} />
}

interface SectionProps {
  section: HomeSection
}

// UNIVERSES — disposition éditoriale, eyebrow signature, headline-xl
function UniversesSection({ section }: SectionProps) {
  return (
    <section className="py-20 sm:py-32 bg-[#f8fafc] relative">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="max-w-3xl mb-16">
          {section.subtitle && (
            <span className="eyebrow mb-5">
              {section.subtitle}
            </span>
          )}
          <h2 className="headline-xl mb-5">
            {section.title}
          </h2>
          <StripHtml
            html={section.content}
            className="body-lead [&_p]:m-0"
          />
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <Link
            to="/bnssa"
            aria-label="Découvrir l'univers BNSSA"
            className="group relative overflow-hidden rounded-3xl p-10 sm:p-14 text-white ease-premium transition-transform duration-500 hover:-translate-y-1.5 flex flex-col min-h-[360px]"
            style={{
              background: 'linear-gradient(135deg,#ef4444 0%,#dc2626 55%,#b91c1c 100%)',
              boxShadow: 'rgba(220,38,38,0.30) 0px 16px 40px, inset 0 1px 0 rgba(255,255,255,0.12)',
            }}
          >
            <div className="absolute -top-16 -right-16 w-72 h-72 bg-white/10 rounded-full blur-3xl pointer-events-none transition-transform duration-700 group-hover:scale-110" />
            <div className="absolute inset-0 bg-gradient-to-br from-white/5 via-transparent to-black/10 pointer-events-none" />
            <div className="relative">
              <span className="inline-flex items-center gap-2 text-[10px] font-bold uppercase tracking-[0.22em] text-white/70 mb-6">
                <span className="w-6 h-px bg-white/50" />
                Espace BNSSA
              </span>
              <LifeBuoy className="w-11 h-11 mb-7 opacity-95" strokeWidth={1.6} />
              <h3 className="text-3xl sm:text-4xl font-black tracking-[-0.03em] leading-[1.02] mb-4 text-white">
                Je suis BNSSA
              </h3>
              <p className="text-[15px] text-red-50/90 leading-[1.6] mb-10 max-w-md">
                Offres saisonnières, CDI, missions auto-entrepreneur, recyclages PSE, salaires & guides.
              </p>
              <span className="inline-flex items-center gap-2 text-sm font-bold mt-auto">
                Entrer dans l'univers BNSSA
                <ArrowUpRight className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" strokeWidth={2.4} />
              </span>
            </div>
          </Link>

          <Link
            to="/maitre-nageur-sauveteur"
            aria-label="Découvrir l'univers MNS"
            className="group relative overflow-hidden rounded-3xl p-10 sm:p-14 text-white ease-premium transition-transform duration-500 hover:-translate-y-1.5 flex flex-col min-h-[360px]"
            style={{
              background: 'linear-gradient(135deg,#3b82f6 0%,#2563eb 55%,#1d4ed8 100%)',
              boxShadow: 'rgba(37,99,235,0.30) 0px 16px 40px, inset 0 1px 0 rgba(255,255,255,0.12)',
            }}
          >
            <div className="absolute -top-16 -right-16 w-72 h-72 bg-white/10 rounded-full blur-3xl pointer-events-none transition-transform duration-700 group-hover:scale-110" />
            <div className="absolute inset-0 bg-gradient-to-br from-white/5 via-transparent to-black/10 pointer-events-none" />
            <div className="relative">
              <span className="inline-flex items-center gap-2 text-[10px] font-bold uppercase tracking-[0.22em] text-white/70 mb-6">
                <span className="w-6 h-px bg-white/50" />
                Espace MNS
              </span>
              <Compass className="w-11 h-11 mb-7 opacity-95" strokeWidth={1.6} />
              <h3 className="text-3xl sm:text-4xl font-black tracking-[-0.03em] leading-[1.02] mb-4 text-white">
                Je suis MNS
              </h3>
              <p className="text-[15px] text-blue-50/90 leading-[1.6] mb-10 max-w-md">
                Piscines municipales, centres aquatiques, auto-entrepreneur, enseignement, aquafitness, CAEPMNS.
              </p>
              <span className="inline-flex items-center gap-2 text-sm font-bold mt-auto">
                Entrer dans l'univers MNS
                <ArrowUpRight className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" strokeWidth={2.4} />
              </span>
            </div>
          </Link>
        </div>
      </div>
    </section>
  )
}

// MISSION — eyebrow blanc, headline serré, sous-cartes en glass minimal
function MissionSection({ section }: SectionProps) {
  return (
    <section className="relative py-24 sm:py-32 overflow-hidden bg-[#0b1220]">
      <div className="absolute inset-0">
        <img src={MISSION_BG} alt="Métiers aquatiques" className="w-full h-full object-cover opacity-25" loading="lazy" />
        <div className="absolute inset-0 bg-gradient-to-r from-[#0b1220] via-[#0b1220]/85 to-[#0b1220]/55" />
      </div>
      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="max-w-2xl">
          {section.subtitle && (
            <span className="inline-flex items-center gap-2.5 text-[10px] font-bold uppercase tracking-[0.22em] text-white/60 mb-6">
              <span className="w-6 h-px bg-white/40" />
              {section.subtitle}
            </span>
          )}
          <h2 className="text-4xl sm:text-5xl lg:text-6xl font-black text-white tracking-[-0.035em] mb-6 leading-[0.98]"
            style={{ fontFamily: "'Bricolage Grotesque', Inter, system-ui, sans-serif" }}>
            {section.title}
          </h2>
          <StripHtml
            html={section.content}
            className="text-[16px] text-gray-300 leading-[1.65] mb-10 max-w-xl [&_a]:text-blue-300 [&_a]:underline [&_strong]:text-white"
          />
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            {[
              { icon: <ShieldCheck className="w-5 h-5 text-red-400" strokeWidth={2} />, label: 'Offres vérifiées', desc: 'Chaque annonce est validée par notre équipe' },
              { icon: <Award className="w-5 h-5 text-blue-400" strokeWidth={2} />, label: 'Profils certifiés', desc: 'Dossiers auto-entrepreneur validés' },
              { icon: <Heart className="w-5 h-5 text-emerald-400" strokeWidth={2} />, label: '100 % gratuit', desc: 'Inscription et candidature gratuites' },
            ].map((item) => (
              <div key={item.label} className="flex flex-col gap-3 bg-white/[0.04] border border-white/[0.08] rounded-2xl p-5 ease-premium transition-colors duration-300 hover:bg-white/[0.06] hover:border-white/[0.12]">
                <div className="flex-shrink-0">{item.icon}</div>
                <div>
                  <div className="text-[13px] font-bold text-white mb-1.5 tracking-[-0.005em]">{item.label}</div>
                  <div className="text-[12px] text-gray-400 leading-[1.55]">{item.desc}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  )
}

// JOBS BNSSA — header asymétrique, JobCardHome premium, dernières annonces réelles
function JobsBnssaSection({ section }: SectionProps) {
  const featured = useLatestJobsByCategory('BNSSA', 3)
  return (
    <section className="py-20 sm:py-28 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-end justify-between flex-wrap gap-6 mb-12">
          <div className="max-w-xl">
            {section.subtitle && (
              <span className="eyebrow eyebrow-red mb-4">
                {section.subtitle}
              </span>
            )}
            <h2 className="headline-lg">
              {section.title}
            </h2>
          </div>
          {section.ctaLabel && section.ctaUrl && (
            <Link to={section.ctaUrl} className="group inline-flex items-center gap-1.5 text-sm font-bold text-red-600 hover:text-red-700 link-premium" style={{ textDecorationColor: 'rgba(220,38,38,0.3)' }}>
              {section.ctaLabel}
              <ArrowUpRight className="w-4 h-4 transition-transform duration-200 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" strokeWidth={2.4} />
            </Link>
          )}
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {featured.map((job) => (
            <JobCardHome key={job.id} job={job} />
          ))}
        </div>
      </div>
    </section>
  )
}

// JOBS MNS — pendant bleu, dernières annonces réelles
function JobsMnsSection({ section }: SectionProps) {
  const featured = useLatestJobsByCategory('MNS', 3)
  return (
    <section className="py-20 sm:py-28 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-end justify-between flex-wrap gap-6 mb-12">
          <div className="max-w-xl">
            {section.subtitle && (
              <span className="eyebrow eyebrow-blue mb-4">
                {section.subtitle}
              </span>
            )}
            <h2 className="headline-lg">
              {section.title}
            </h2>
          </div>
          {section.ctaLabel && section.ctaUrl && (
            <Link to={section.ctaUrl} className="group inline-flex items-center gap-1.5 text-sm font-bold text-blue-600 hover:text-blue-700 link-premium" style={{ textDecorationColor: 'rgba(37,99,235,0.3)' }}>
              {section.ctaLabel}
              <ArrowUpRight className="w-4 h-4 transition-transform duration-200 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" strokeWidth={2.4} />
            </Link>
          )}
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {featured.map((job) => (
            <JobCardHome key={job.id} job={job} />
          ))}
        </div>
      </div>
    </section>
  )
}

// FORMATIONS — eyebrow + headline + 3 cartes premium hiérarchisées
function FormationsSection({ section }: SectionProps) {
  return (
    <section className="py-20 sm:py-28 bg-[#f8fafc]">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="max-w-3xl mb-14">
          {section.subtitle && (
            <span className="eyebrow eyebrow-amber mb-5">
              {section.subtitle}
            </span>
          )}
          <h2 className="headline-xl mb-5">
            {section.title}
          </h2>
          <StripHtml
            html={section.content}
            className="body-lead [&_p]:m-0"
          />
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <Link
            to="/bnssa/formations"
            className="group relative overflow-hidden rounded-3xl p-9 text-white ease-premium transition-transform duration-500 hover:-translate-y-1.5 flex flex-col min-h-[300px]"
            style={{ background: 'linear-gradient(135deg,#ef4444 0%,#dc2626 55%,#b91c1c 100%)', boxShadow: 'rgba(220,38,38,0.25) 0px 12px 30px, inset 0 1px 0 rgba(255,255,255,0.12)' }}
          >
            <div className="absolute -top-12 -right-12 w-52 h-52 bg-white/10 rounded-full blur-3xl pointer-events-none transition-transform duration-700 group-hover:scale-110" />
            <div className="relative flex flex-col h-full">
              <span className="inline-flex items-center gap-2 text-[10px] font-bold uppercase tracking-[0.2em] text-white/70 mb-5">
                <span className="w-5 h-px bg-white/50" />
                BNSSA
              </span>
              <ShieldCheck className="w-9 h-9 mb-5 opacity-95" strokeWidth={1.6} />
              <h3 className="text-2xl font-black mb-3 tracking-[-0.025em] leading-tight">Formations BNSSA</h3>
              <p className="text-[14px] text-red-50/90 leading-[1.6] mb-7">Préparation BNSSA, recyclages PSE1/PSE2, secourisme.</p>
              <span className="inline-flex items-center gap-2 text-sm font-bold mt-auto">
                Voir les sessions BNSSA
                <ArrowUpRight className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" strokeWidth={2.4} />
              </span>
            </div>
          </Link>

          <Link
            to="/maitre-nageur-sauveteur/formations"
            className="group relative overflow-hidden rounded-3xl p-9 text-white ease-premium transition-transform duration-500 hover:-translate-y-1.5 flex flex-col min-h-[300px]"
            style={{ background: 'linear-gradient(135deg,#3b82f6 0%,#2563eb 55%,#1d4ed8 100%)', boxShadow: 'rgba(37,99,235,0.25) 0px 12px 30px, inset 0 1px 0 rgba(255,255,255,0.12)' }}
          >
            <div className="absolute -top-12 -right-12 w-52 h-52 bg-white/10 rounded-full blur-3xl pointer-events-none transition-transform duration-700 group-hover:scale-110" />
            <div className="relative flex flex-col h-full">
              <span className="inline-flex items-center gap-2 text-[10px] font-bold uppercase tracking-[0.2em] text-white/70 mb-5">
                <span className="w-5 h-px bg-white/50" />
                MNS
              </span>
              <Waves className="w-9 h-9 mb-5 opacity-95" strokeWidth={1.6} />
              <h3 className="text-2xl font-black mb-3 tracking-[-0.025em] leading-tight">Formations MNS</h3>
              <p className="text-[14px] text-blue-50/90 leading-[1.6] mb-7">BPJEPS AAN, BEESAN, CAEPMNS, perfectionnements aquafitness.</p>
              <span className="inline-flex items-center gap-2 text-sm font-bold mt-auto">
                Voir les sessions MNS
                <ArrowUpRight className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" strokeWidth={2.4} />
              </span>
            </div>
          </Link>

          <div className="card-premium p-9 flex flex-col min-h-[300px]">
            <span className="eyebrow eyebrow-amber mb-5">
              Catalogue
            </span>
            <GraduationCap className="w-9 h-9 mb-5 text-amber-500" strokeWidth={1.6} />
            <h3 className="text-2xl font-black mb-4 tracking-[-0.025em] leading-tight text-[#0b1220]">Catalogue complet</h3>
            <ul className="space-y-2.5 mb-7">
              {['Organismes agréés Ministère des Sports', 'Financement CPF, OPCO, Pôle Emploi', 'Sessions partout en France & DOM-TOM'].map((item) => (
                <li key={item} className="flex items-start gap-2.5 text-[13px] text-gray-600 leading-[1.5]">
                  <CheckCircle2 className="w-4 h-4 text-emerald-500 flex-shrink-0 mt-0.5" strokeWidth={2.2} />
                  <span>{item}</span>
                </li>
              ))}
            </ul>
            <div className="mt-auto flex flex-col gap-2.5">
              <Link to="/formations" className="btn-premium bg-[#0b1220] hover:bg-black text-white text-[13px]">
                <GraduationCap className="w-4 h-4" strokeWidth={2.2} />
                Voir le catalogue
              </Link>
              <Link to="/inscription?role=organisme" className="inline-flex items-center justify-center gap-2 bg-white border border-gray-200 text-gray-700 hover:bg-gray-50 hover:border-gray-300 px-5 py-3 rounded-xl font-bold text-[13px] transition-all duration-200">
                Annoncer une formation
              </Link>
            </div>
          </div>
        </div>
      </div>
    </section>
  )
}

// TESTIMONIALS — citation iconique + cartes premium
function TestimonialsSection({ section }: SectionProps) {
  const top = testimonials.slice(0, 3)
  return (
    <section className="py-20 sm:py-28 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="max-w-3xl mb-14">
          {section.subtitle && (
            <span className="eyebrow mb-5">
              {section.subtitle}
            </span>
          )}
          <h2 className="headline-xl mb-5">
            {section.title}
          </h2>
          <StripHtml
            html={section.content}
            className="body-lead [&_p]:m-0"
          />
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {top.map((t) => {
            const isBNSSA = t.category === 'BNSSA'
            const isMNS = t.category === 'MNS'
            const accentColor = isBNSSA ? '#dc2626' : isMNS ? '#2563eb' : '#6b7280'
            return (
              <div
                key={t.id}
                className="card-premium p-8 flex flex-col"
              >
                <Quote className="w-8 h-8 mb-5 opacity-15" style={{ color: accentColor }} strokeWidth={1.5} />
                <div className="flex items-center gap-1 mb-4">
                  {Array.from({ length: t.rating }).map((_, i) => (
                    <Star key={i} className="w-3.5 h-3.5 fill-amber-400 text-amber-400" />
                  ))}
                </div>
                <p className="text-[14.5px] text-gray-700 leading-[1.65] mb-7 flex-1">"{t.content}"</p>
                <div className="flex items-center gap-3 pt-5 border-t border-gray-100">
                  <div
                    className={`w-10 h-10 rounded-full flex items-center justify-center font-bold text-sm flex-shrink-0 ${
                      isBNSSA ? 'bg-red-50 text-red-700 border border-red-100' : isMNS ? 'bg-blue-50 text-blue-700 border border-blue-100' : 'bg-gray-100 text-gray-600'
                    }`}
                  >
                    {t.avatar}
                  </div>
                  <div>
                    <div className="text-[13.5px] font-bold text-[#0b1220] tracking-[-0.005em]">{t.name}</div>
                    <div className="text-[12px] text-gray-500 mt-0.5">{t.role} · {t.city}</div>
                  </div>
                </div>
              </div>
            )
          })}
        </div>
      </div>
    </section>
  )
}

// FAQ — disposition resserrée, accordéons premium
function FaqSection({ section }: SectionProps) {
  const top = faqs.slice(0, 4)
  return (
    <section className="py-20 sm:py-28 bg-[#f8fafc]">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="mb-12">
          {section.subtitle && (
            <span className="eyebrow mb-5">
              {section.subtitle}
            </span>
          )}
          <h2 className="headline-lg mb-4">
            {section.title}
          </h2>
          <StripHtml
            html={section.content}
            className="body-lead [&_p]:m-0"
          />
        </div>
        <div className="space-y-3">
          {top.map((f) => (
            <details
              key={f.id}
              className="card-premium group overflow-hidden"
            >
              <summary className="flex items-center justify-between gap-4 px-6 py-5 cursor-pointer list-none font-bold text-[#0b1220] text-[14.5px] hover:bg-gray-50/40 transition-colors tracking-[-0.005em]">
                <span>{f.question}</span>
                <span className="w-7 h-7 bg-gray-50 border border-gray-150 rounded-full flex items-center justify-center text-gray-500 group-open:rotate-45 group-open:bg-[#0b1220] group-open:text-white group-open:border-[#0b1220] transition-all duration-300 flex-shrink-0 text-base font-light">+</span>
              </summary>
              <div className="px-6 pb-6 pt-1">
                <p className="text-[13.5px] text-gray-600 leading-[1.7]">{f.answer}</p>
              </div>
            </details>
          ))}
        </div>
        {section.ctaLabel && section.ctaUrl && (
          <div className="mt-10">
            <Link to={section.ctaUrl} className="group inline-flex items-center gap-2 text-sm font-bold text-[#0b1220] link-premium">
              {section.ctaLabel}
              <ArrowUpRight className="w-4 h-4 transition-transform duration-200 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" strokeWidth={2.4} />
            </Link>
          </div>
        )}
      </div>
    </section>
  )
}

// CTA FINAL — moment éditorial dramatique
function CtaFinalSection({ section }: SectionProps) {
  return (
    <section className="relative py-28 sm:py-36 overflow-hidden bg-[#0a0a0a]">
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute -top-40 -left-40 w-[600px] h-[600px] bg-red-600/15 rounded-full blur-[120px]" />
        <div className="absolute -bottom-40 -right-40 w-[600px] h-[600px] bg-blue-600/15 rounded-full blur-[120px]" />
      </div>
      <div className="relative max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
        {section.subtitle && (
          <div className="inline-flex items-center gap-2 bg-white/[0.06] border border-white/[0.1] rounded-full px-5 py-2 text-[11px] font-bold uppercase tracking-[0.18em] text-white/70 mb-10">
            <Sparkles className="w-3 h-3" strokeWidth={2.4} />
            {section.subtitle}
          </div>
        )}
        <h2 className="text-4xl sm:text-6xl lg:text-7xl font-black text-white mb-6 tracking-[-0.045em] leading-[0.95]"
          style={{ fontFamily: "'Bricolage Grotesque', Inter, system-ui, sans-serif" }}>
          {section.title}
        </h2>
        <StripHtml
          html={section.content}
          className="text-[17px] text-gray-400 mb-12 max-w-2xl mx-auto leading-[1.65] [&_p]:m-0 [&_strong]:text-white"
        />
        <div className="flex flex-col sm:flex-row gap-3 justify-center">
          {section.ctaLabel && section.ctaUrl && (
            <Link
              to={section.ctaUrl}
              className="btn-premium bg-white text-[#0b1220] hover:bg-gray-50 text-[14px]"
            >
              <Users className="w-4 h-4" strokeWidth={2.2} />
              {section.ctaLabel}
            </Link>
          )}
          {section.ctaLabel2 && section.ctaUrl2 && (
            <Link
              to={section.ctaUrl2}
              className="bg-white/[0.06] border border-white/[0.12] text-white px-6 py-3.5 rounded-xl font-bold text-[14px] hover:bg-white/[0.1] hover:border-white/[0.2] transition-all duration-300 inline-flex items-center justify-center gap-2"
            >
              <Briefcase className="w-4 h-4" strokeWidth={2.2} />
              {section.ctaLabel2}
            </Link>
          )}
        </div>
      </div>
    </section>
  )
}

// DISPATCHER
export default function HomeSectionsRenderer({ sections }: { sections: HomeSection[] }) {
  return (
    <>
      {sections.map((section) => {
        if (!section?.isActive) return null
        switch (section.sectionType) {
          case 'hero':
            return <HomeHeroSection key={section._id} section={section} />
          case 'universes':
            return <UniversesSection key={section._id} section={section} />
          case 'mission':
            return <MissionSection key={section._id} section={section} />
          case 'jobs-bnssa':
            return <JobsBnssaSection key={section._id} section={section} />
          case 'jobs-mns':
            return <JobsMnsSection key={section._id} section={section} />
          case 'formations':
            return <FormationsSection key={section._id} section={section} />
          case 'testimonials':
            return <TestimonialsSection key={section._id} section={section} />
          case 'faq':
            return <FaqSection key={section._id} section={section} />
          case 'cta-final':
            return <CtaFinalSection key={section._id} section={section} />
          case 'custom':
          default:
            return <HomeCustomSection key={section._id} section={section} />
        }
      })}
    </>
  )
}
